import { Reducer } from 'interfaces'
import { combineReducers } from 'redux'
import {
  CLOSE_PEER_GROUP_FORM,
  HIDE_PEERGROUP_POPUP,
  REGISTER_PEERGROUP_POPUP_TIMEOUT,
  SET_SHOWN_SIDEBAR_PANE,
  SHOW_PEER_GROUP_CREATE_FORM,
  SHOW_PEER_GROUP_EDIT_FORM,
  SHOW_PEERGROUP_POPUP,
  SET_NAVIGATION_COUNTERS,
  STASH_NAVIGATION_COUNTERS,
  UNSTASH_NAVIGATION_COUNTERS,
} from 'actions/sidebarActions'
import { PAGE_CHANGED } from 'actions/globalActions'
import { SidebarPane, SidebarState, NavigationCounters, NavigationCountersStash, NavigationCountersActual } from 'interfaces/sidebar'
import { TripleFormMode } from 'interfaces/form'

import { getDefaultRelation, peergroupFiltersMap } from 'helpers/peergroupsHelpers'
import { Peergroup, PeergroupFormAppState, PeergroupFormField, PeergroupPopupDetails, RelationId } from 'interfaces/peergroups'
import { EMPTY_PEER_GROUP } from 'constants/peergroups'
import {
  ADD_PEER_GROUP_FILTER,
  DELETE_PEER_GROUP_FILTER,
  DELETE_RELATION,
  FIND_PEER_GROUP,
  RANDOMIZE_PEER_GROUP_RELATION,
  RESET_PEERGROUP_FILTERS,
  SELECT_RELATION,
  SET_FOUND_PEER_GROUP,
  SET_PEER_GROUP_FILTERS,
  SET_PEER_GROUPS,
  SET_PEERGROUP_FILTERS_POPUP_SHOWN,
  SET_RANDOM_RELATION,
  TOGGLE_SIDEBAR_PEER_GROUPS,
} from 'actions/peergroupActions'
import { needDiscardData, PEERGROUPS_DISCARD_TRIGGERS } from 'helpers/discardTriggerHelpers'
import { SET_VIEWPORT_MATCH } from 'actions/viewportActions'
import { TOGGLE_DISMISS_INSIGHT } from 'actions/insightsActions'
import { isSuccessAction } from 'helpers/actionsHelpers'

// ===========================================================================//
//                                SIDEBAR STATE                               //
// ===========================================================================//

const isPeergroupSearching: Reducer<boolean> = (state = false, action) => {
  switch (action.type) {
    case FIND_PEER_GROUP:
      return true

    case SET_FOUND_PEER_GROUP:
      return false

    default:
      return state
  }
}

const DEFAULT_PEER_GROUP_FORM_MODE: TripleFormMode<RelationId> = {
  mode: 'hidden',
}
const formMode: Reducer<TripleFormMode<RelationId>> = (state = DEFAULT_PEER_GROUP_FORM_MODE, action) => {
  switch (action.type) {
    case SHOW_PEER_GROUP_CREATE_FORM:
      return {
        mode: 'create',
      }

    case SHOW_PEER_GROUP_EDIT_FORM:
      return {
        mode: 'edit',
        id: action.payload,
      }

    case CLOSE_PEER_GROUP_FORM:
      return DEFAULT_PEER_GROUP_FORM_MODE

    default:
      return state
  }
}

const selectedRelationId: Reducer<RelationId | null> = (state = null, action) => {
  if (needDiscardData(PEERGROUPS_DISCARD_TRIGGERS)(action)) {
    return null
  }

  switch (action.type) {
    case SELECT_RELATION:
      return action.payload

    case SET_PEER_GROUPS: {
      const { relations } = action.payload
      const defaultRelation = getDefaultRelation(relations)

      return defaultRelation ? defaultRelation.id : null
    }

    default:
      return state
  }
}

const isRandomizing: Reducer<boolean> = (state = false, action) => {
  switch (action.type) {
    case RANDOMIZE_PEER_GROUP_RELATION:
      return true

    case SET_RANDOM_RELATION:
      return false

    default:
      return state
  }
}

// ===========================================================================//
//                                PEER GROUP FORM                             //
// ===========================================================================//

const isFiltersPopupShown: Reducer<boolean> = (state = false, action) => {
  switch (action.type) {
    case SET_PEERGROUP_FILTERS_POPUP_SHOWN:
      return action.payload

    case CLOSE_PEER_GROUP_FORM:
    case SET_SHOWN_SIDEBAR_PANE:
      return false

    default:
      return state
  }
}

const selectedFilters: Reducer<PeergroupFormField[]> = (state = [], action) => {
  switch (action.type) {
    case SET_PEER_GROUP_FILTERS:
      return action.payload.map((name) => peergroupFiltersMap[name])

    case ADD_PEER_GROUP_FILTER:
      const foundElement = peergroupFiltersMap[action.payload]
      return foundElement ? [...state, foundElement] : state

    case DELETE_PEER_GROUP_FILTER: {
      const { filter } = action.payload
      return state.filter((option) => option.name !== filter)
    }

    case CLOSE_PEER_GROUP_FORM:
    case RESET_PEERGROUP_FILTERS:
      return []

    case SET_SHOWN_SIDEBAR_PANE:
      return action.payload === 'form' ? state : []

    default:
      return state
  }
}

const currentGroup: Reducer<Peergroup | null> = (state = null, action) => {
  switch (action.type) {
    case SET_FOUND_PEER_GROUP:
      if (action.payload === null) {
        return EMPTY_PEER_GROUP
      }
      return action.payload

    case RESET_PEERGROUP_FILTERS:
      return null

    default:
      return state
  }
}

const shownPaneReducer: Reducer<SidebarPane | null> = (state = null, action) => {
  switch (action.type) {
    case SHOW_PEER_GROUP_CREATE_FORM:
    case SHOW_PEER_GROUP_EDIT_FORM:
      return 'form'

    case CLOSE_PEER_GROUP_FORM:
      return 'groups'

    case TOGGLE_SIDEBAR_PEER_GROUPS:
      return action.payload ? 'groups' : null

    case SET_SHOWN_SIDEBAR_PANE:
      return action.payload

    case PAGE_CHANGED:
      return null
  }

  return state
}

const INITIAL_STATE = {}
const peergroupPopupReducer: Reducer<PeergroupPopupDetails> = (state = INITIAL_STATE, action) => {
  switch (action.type) {
    case SHOW_PEERGROUP_POPUP:
      return action.payload

    case HIDE_PEERGROUP_POPUP:
    case DELETE_RELATION:
      return state.relationId === action.payload ? INITIAL_STATE : state

    case SET_SHOWN_SIDEBAR_PANE:
      return action.payload !== 'groups' ? INITIAL_STATE : state

    case SHOW_PEER_GROUP_CREATE_FORM:
    case SHOW_PEER_GROUP_EDIT_FORM:
    case SET_VIEWPORT_MATCH:
      return INITIAL_STATE

    case REGISTER_PEERGROUP_POPUP_TIMEOUT:
      return {
        ...state,
        timeoutId: action.payload,
      }

    default:
      return state
  }
}

const countersActualReducer: Reducer<Partial<NavigationCountersActual>> = (prevState = {}, action) => {
  switch (action.type) {
    case SET_NAVIGATION_COUNTERS:
      return {
        ...prevState,
        ...action.payload,
      }

    case TOGGLE_DISMISS_INSIGHT: {
      const { is_dismissed, insight } = action.payload
      if (is_dismissed && insight.placement === 'opportunities_main' && insight.is_new) {
        const prevCounter = 'unseen_opportunities' in prevState ? prevState.unseen_opportunities : 0
        if (prevCounter) {
          return {
            ...prevState,
            unseen_opportunities: prevCounter - 1,
          }
        }
      }

      return prevState
    }
  }

  return prevState
}

const countersStashReducer: Reducer<NavigationCountersStash> = (prevState = {}, action) => {
  switch (action.type) {
    case STASH_NAVIGATION_COUNTERS: {
      return {
        ...prevState,
        ...action.payload,
      }
    }

    case UNSTASH_NAVIGATION_COUNTERS: {
      if (isSuccessAction(action)) {
        return {}
      }

      return prevState
    }
  }
  return prevState
}

const countersReducer = combineReducers<NavigationCounters>({
  actual: countersActualReducer,
  stash: countersStashReducer,
})

export default combineReducers<SidebarState>({
  counters: countersReducer,
  shownPane: shownPaneReducer,
  peergroupPopup: peergroupPopupReducer,

  selectedRelationId,
  isRandomizing,
  peergroupForm: combineReducers<PeergroupFormAppState>({
    isFiltersPopupShown,
    isPeergroupSearching,
    selectedFilters,
    currentGroup,
    formMode,
  }),
})
